import * as Yup from 'yup';
import { useCallback, useEffect, useMemo, useRef } from 'react';
// import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import closeFill from '@iconify/icons-eva/close-fill';
// hooks
import useAuth from 'src/hooks/useAuth';
import useIsMountedRef from 'src/hooks/useIsMountedRef';
// material
import { Stack, Alert, TextField, Button } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// import { MIconButton } from 'src/components-minimals/@material-extend';
import { MIconButton } from 'src/components/@material-extend';

import { makeStyles } from '@mui/styles';
import { INITIAL_PATH } from 'src/constants';
import { AVAILABLE_MFA_METHODS, MFA_METHOD_EMAIL, MFA_METHOD_TOTP } from 'src/@types/authentication';
import { useSnackbar } from 'notistack5';

type InitialValues = {
  authCode: string;
  afterSubmit?: string;
};

type VerifyMfaCodeFormProps = {
  activeMfaMethod: AVAILABLE_MFA_METHODS | null; // ユーザーの選択済みMFA方式
};

const useStyles = makeStyles({
  root: {
    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    border: 0,
    borderRadius: 8,
    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
    color: 'white',
    height: 48,
    padding: '0 30px',
  },
});

/**
 * MFA認証コード入力Form
 * @returns VerifyMfaCodeForm
 */
export const VerifyMfaCodeForm = ({ activeMfaMethod }: VerifyMfaCodeFormProps) => {
  // Hooks
  const { verifyMfaCode, moveToLoginWithClearStorage } = useAuth();
  const isMountedRef = useIsMountedRef();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // form
  const inputRef = useRef<HTMLInputElement>(null);
  const VerifyMfaSchema = Yup.object().shape({
    authCode: Yup.string().required(t('validation.required-input', { item: t('mfa.authCode') })),
  });

  // 初期処理
  useEffect(() => {
    // 認証コードtextboxにフォーカス
    if (inputRef?.current) inputRef.current.focus();
  }, []);

  // 認証コード入力を促すメッセージ設定
  const otpRequestMessage = useMemo(() => {
    if (!activeMfaMethod) return 'Cannot find available Mfa Method. Please return to Login form.';
    switch (activeMfaMethod) {
      case MFA_METHOD_EMAIL:
        return t('mfa.requestEmailOtp');
      case MFA_METHOD_TOTP:
        return t('mfa.requestTOTP');
      default:
        return '';
    }
  }, [activeMfaMethod, t]);

  // 戻るボタン押下
  const handleBack = async () => {
    // Session情報をクリアしてログイン画面へ ※当画面表示時は未ログイン状態なので、logout処理は不要。
    moveToLoginWithClearStorage();
  };

  const formik = useFormik<InitialValues>({
    initialValues: {
      authCode: '',
    },
    validationSchema: VerifyMfaSchema,
    onSubmit: async (values, { setErrors, setSubmitting, resetForm }) => {
      try {
        // 入力した認証コードの認証
        await verifyMfaCode(values.authCode);

        // MFA認証成功時
        enqueueSnackbar(t('notification.login.success'), {
          variant: 'success',
          action: (key) => (
            <MIconButton size="small" onClick={() => closeSnackbar(key)}>
              <Icon icon={closeFill} />
            </MIconButton>
          ),
        });
        if (isMountedRef.current) {
          setSubmitting(false);
          // HOME画面へ遷移
          window.location.pathname !== '/'
            ? navigate(window.location.pathname + window.location.search, { replace: true })
            : navigate(INITIAL_PATH, { replace: true });
        }
      } catch (err: any) {
        console.error(err);
        resetForm();
        setSubmitting(false);
        setErrors({ afterSubmit: getErrorMessage(err) });
      }
    },
  });

  // メッセージ管理
  const getErrorMessage = useCallback(
    (err: any): string => {
      if (err instanceof Error) {
        const msg = () => {
          switch (err.name) {
            case 'CodeMismatchException': // 認証コード不一致
              return t('validation.mismatch', { item: t('mfa.authCode') });
            case 'ExpiredCodeException': // 認証コードタイムアウト
              return t('mfa.error.otpExpired');
            case 'NotAuthorizedException':
              if (err.message.includes('session is expired')) return t('mfa.error.otpExpired'); // 認証コードタイムアウト
              if (err.message.includes('Too many invalid credentials attempt')) return t('mfa.error.lockedByTooManyLogin'); // サインイン失敗ロックアウト
              return err.message;
            default:
              return err.message;
          }
        };
        return msg();
      }
      return err.toString();
    },
    [t],
  );

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const classes = useStyles();

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <>
            <label>{otpRequestMessage}</label>

            {errors.afterSubmit && <Alert severity="error">{errors.afterSubmit}</Alert>}
            <TextField
              fullWidth
              size="medium"
              autoComplete="one-time-code"
              type="text"
              label={t('mfa.authCode')}
              {...getFieldProps('authCode')}
              error={Boolean(touched.authCode && errors.authCode)}
              helperText={touched.authCode && errors.authCode}
              inputRef={inputRef}
            />
          </>
          <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isSubmitting} className={classes.root}>
            {t('button.send')}
          </LoadingButton>
          <Button fullWidth size="large" sx={{ mt: 1 }} onClick={handleBack}>
            {t('button.backToLogin')}
          </Button>
        </Stack>
      </Form>
    </FormikProvider>
  );
};
